import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LuBell } from "react-icons/lu";
import { MdOutlineNotificationsActive, MdOutlineNotificationsNone } from "react-icons/md";
import { useWebSocket } from "../../hooks/useWebSocket";
import { deleteEzp, fetchEzpNotifications, markEzpRead, notificationTarget } from "../../utils/ezpNotifications";

// The supplier's bell: their own notifications (ezp_notifications), refreshed every 30 seconds and at once
// when the server pushes "something changed" over the websocket.
export default function SupplierBell() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  const refresh = useCallback(async () => setItems(await fetchEzpNotifications()), []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [refresh]);

  useWebSocket({ onEvent: (e) => e && e.type === "proc_notification" && refresh(), onOpen: refresh });

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => box.current && !box.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const unread = items.filter((n) => n.is_read === 0).length;
  const click = async (n) => {
    if (n.is_read === 0) {
      setItems((list) => list.map((x) => (x.id === n.id ? { ...x, is_read: 1 } : x)));
      markEzpRead(n.id).catch(() => refresh());
    }
    const target = notificationTarget(n, "supplier");
    if (target) {
      setOpen(false);
      navigate(target);
    }
  };
  const clearAll = async () => {
    const all = items;
    setItems([]);
    await Promise.all(all.map((n) => deleteEzp(n.id))).catch(() => refresh());
  };

  return (
    <div ref={box} className="position-relative">
      <button type="button" className="btn p-1 position-relative" aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`} onClick={() => setOpen((o) => !o)} style={{ background: "none", border: "none" }}>
        <LuBell style={{ fontSize: 26, color: "#4B5563" }} />
        {unread > 0 ? (
          <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: 10, padding: "3px 5px" }}>
            {unread}
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="supplier-bell-panel" role="dialog" aria-label="Notifications">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span style={{ fontWeight: 600 }}>Notifications</span>
            {items.length ? (
              <button type="button" className="btn btn-link p-0" style={{ fontSize: 13, textDecoration: "none" }} onClick={clearAll}>
                Clear all
              </button>
            ) : null}
          </div>
          {items.length === 0 ? (
            <div style={{ textAlign: "center", color: "#888", marginTop: 32 }}>You are yet to receive notifications.</div>
          ) : (
            items.map((n) => (
              <div key={n.id} role="button" tabIndex={0} onClick={() => click(n)} onKeyDown={(e) => e.key === "Enter" && click(n)} style={{ borderBottom: "1px solid #eee", padding: "12px 0", cursor: "pointer", background: n.is_read === 0 ? "#f8f9fa" : "transparent", display: "flex", gap: 10 }}>
                {n.is_read === 0 ? <MdOutlineNotificationsActive style={{ color: "#C9194A", fontSize: 20, flexShrink: 0 }} /> : <MdOutlineNotificationsNone style={{ color: "#888", fontSize: 20, flexShrink: 0 }} />}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{n.title}</div>
                  <div style={{ fontSize: 12, color: "#555" }}>{n.message}</div>
                  <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>{new Date(n.created).toLocaleString()}</div>
                </div>
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
